import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingBag, CheckCircle, XCircle, Wallet, Star, Plus, Edit2, Trash2, Power, Store, Clock, ShieldCheck, PackageSearch, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, SAR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Empty } from "@/components/shared";
import { StatusBadge, ORDER_STATUS, PaymentBadge } from "@/lib/status";
import { ImageUploader } from "@/components/ImageUploader";
import { ReturnCard } from "@/components/returns";
import { VehiclePicker } from "@/components/VehiclePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const NEXT_STATUS = {
  new: [["accepted", "قبول"], ["rejected", "رفض"]],
  accepted: [["preparing", "بدء التجهيز"]],
  preparing: [["ready", "جاهز للاستلام"], ["delivering", "إرسال للتوصيل"]],
  ready: [["completed", "إتمام الطلب"]],
  delivering: [["completed", "إتمام الطلب"]],
};

export default function SellerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "overview");
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState(false);

  const load = () => api.get("/seller/stats").then(({ data }) => { setStats(data); setPending(data.yard.status !== "approved"); }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (stats === null) return <Loader />;
  const yard = stats.yard;

  if (pending) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--amber)]/10 text-[var(--amber)] mb-4">
          {yard.status === "rejected" ? <XCircle size={32} /> : <Clock size={32} />}
        </span>
        <h1 className="font-head text-2xl font-black text-white mb-3">
          {yard.status === "rejected" ? "تم رفض طلب حسابك" : "حسابك بانتظار المراجعة"}
        </h1>
        {yard.status === "rejected" ? (
          <p className="text-[var(--text-muted)]">السبب: {yard.rejection_reason || "لم يُذكر"}. يرجى تعديل بياناتك والتواصل مع الدعم لإعادة الإرسال.</p>
        ) : (
          <p className="text-[var(--text-muted)]">شكرًا لتسجيلك في مخزون كار. يقوم فريقنا بمراجعة بياناتك ورقم سجلك التجاري، وسيتم إشعارك فور الموافقة لتبدأ بإضافة القطع.</p>
        )}
        <div className="ts-card p-4 mt-6 text-right">
          <p className="text-sm text-white font-bold">{yard.name}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{yard.city} · {yard.address}</p>
        </div>
      </div>
    );
  }

  const cards = [
    { icon: Package, label: "إجمالي القطع", value: stats.parts_total, color: "#3B82F6" },
    { icon: ShieldCheck, label: "قطع متاحة", value: stats.parts_active, color: "#10B981" },
    { icon: ShoppingBag, label: "طلبات جديدة", value: stats.orders_new, color: "#F59E0B" },
    { icon: CheckCircle, label: "طلبات مكتملة", value: stats.orders_completed, color: "#10B981" },
    { icon: XCircle, label: "ملغاة/مرفوضة", value: stats.orders_cancelled, color: "#EF4444" },
    { icon: Wallet, label: `المبيعات (${SAR})`, value: stats.sales.toLocaleString("ar"), color: "#F59E0B" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl overflow-hidden bg-[var(--bg-secondary)]">
            {yard.logo ? <img src={fileUrl(yard.logo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-head text-xl font-black text-[var(--amber)]">{yard.name?.[0]}</div>}
          </div>
          <div>
            <h1 className="font-head text-xl font-black text-white flex items-center gap-1.5">{yard.name} <ShieldCheck size={16} className="text-[var(--emerald)]" /></h1>
            <p className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Star size={12} fill="#F59E0B" stroke="#F59E0B" /> {stats.rating} · {yard.city}</p>
          </div>
        </div>
        <Link to={`/yard/${yard.id}`} className="text-sm text-[var(--amber)]">عرض المتجر ↗</Link>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {[["overview", "نظرة عامة"], ["parts", "إدارة القطع"], ["requests", "طلبات العملاء"], ["offers", "عروضي"], ["orders", "الطلبات"], ["returns", "الاستبدال والاسترجاع"], ["payment", "بيانات الدفع"]].map(([k, l]) => (
          <button key={k} data-testid={`seller-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold ${tab === k ? "bg-[var(--amber)] text-[#0B0F17]" : "bg-[var(--bg-card)] text-[var(--text-muted)]"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <SubscriptionBanner />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {cards.map((c, i) => (
              <div key={i} className="ts-card p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl mb-3" style={{ backgroundColor: `${c.color}22`, color: c.color }}><c.icon size={20} /></span>
                <p className="font-head text-2xl font-black text-white">{c.value}</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "parts" && <PartsManager onChange={load} />}
      {tab === "requests" && <RequestsForSeller />}
      {tab === "offers" && <MyOffers />}
      {tab === "orders" && <OrdersManager />}
      {tab === "returns" && <SellerReturns />}
      {tab === "payment" && <PaymentInfo />}
    </div>
  );
}

function SubscriptionBanner() {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/seller/subscription").then(({ data }) => setSub(data)).catch(() => {}); }, []);
  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/tap/subscription/charge", {});
      toast.info("جارٍ التحويل لبوابة الدفع...");
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  if (!sub) return null;
  const active = sub.status === "active";
  const trialing = sub.status === "trial";
  const color = active ? "#10B981" : trialing ? "#F59E0B" : "#EF4444";
  return (
    <div className="ts-card p-5" data-testid="subscription-banner" style={{ borderColor: `${color}55` }}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Wallet size={18} style={{ color }} />
            <h3 className="font-bold text-white">
              {active ? "اشتراكك فعّال" : trialing ? "أنت في الفترة التجريبية المجانية" : "انتهت فترتك التجريبية"}
            </h3>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            {active
              ? `متبقٍ ${sub.sub_days_left} يوم · حتى ${(sub.subscription_paid_until || "").slice(0, 10)}`
              : trialing
                ? `متبقٍ ${sub.trial_days_left} يوم من التجربة المجانية (٣ أشهر) — استمتع بكل مزايا المنصة مجانًا`
                : "اشترك للاستمرار في الاستفادة من كل مزايا متجرك على المنصة"}
          </p>
          {trialing && sub.trial_days_left <= 14 && (
            <p data-testid="trial-warning" className="text-xs font-bold mt-1" style={{ color: "#F59E0B" }}>
              ⚠️ تنبيه: ستنتهي فترتك التجريبية خلال {sub.trial_days_left} يوم
            </p>
          )}
        </div>
        {!active && (
          <div className="shrink-0">
            <button data-testid="subscribe-standard" disabled={busy} onClick={subscribe}
              className="btn-amber !py-2 !px-4 text-sm disabled:opacity-60">اشترك الآن · {sub.subscription_price} {SAR}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function SellerReturns() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/seller/returns").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  if (items === null) return <Loader />;
  if (items.length === 0) return <Empty text="لا توجد طلبات استبدال أو استرجاع." />;
  return (
    <div className="space-y-4">
      {items.map((r) => <ReturnCard key={r.id} req={r} role="seller" onUpdated={load} />)}
    </div>
  );
}

function PartsManager({ onChange }) {
  const [parts, setParts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/seller/parts").then(({ data }) => setParts(data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("حذف القطعة؟")) return;
    await api.delete(`/parts/${id}`); toast.success("تم الحذف"); load(); onChange();
  };
  const toggle = async (id) => { await api.post(`/parts/${id}/toggle`); load(); };

  if (parts === null) return <Loader />;
  return (
    <div>
      <button data-testid="seller-add-part-button" onClick={() => { setEditing(null); setOpen(true); }} className="btn-amber mb-5"><Plus size={18} /> إضافة قطعة</button>
      {parts.length === 0 ? <Empty text="لا توجد قطع بعد. أضف أول قطعة!" /> : (
        <div className="space-y-3">
          {parts.map((p) => (
            <div key={p.id} data-testid={`seller-part-${p.id}`} className="ts-card p-3 flex items-center gap-3">
              <img src={p.images?.[0] ? fileUrl(p.images[0]) : "https://via.placeholder.com/80"} alt="" className="h-14 w-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white text-sm truncate">{p.name}</p>
                <p className="text-xs text-[var(--text-muted)]">{p.price.toLocaleString("ar")} {SAR} · كمية {p.quantity}</p>
              </div>
              <span className={`text-[10px] px-2 py-1 rounded-lg ${p.status === "active" ? "bg-[var(--emerald)]/15 text-[var(--emerald)]" : "bg-[var(--text-subtle)]/15 text-[var(--text-muted)]"}`}>{p.status === "active" ? "متاحة" : "موقوفة"}</span>
              <div className="flex gap-1">
                <button data-testid={`toggle-part-${p.id}`} onClick={() => toggle(p.id)} className="p-2 text-[var(--text-muted)] hover:text-[var(--amber)]"><Power size={16} /></button>
                <button data-testid={`edit-part-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }} className="p-2 text-[var(--text-muted)] hover:text-[var(--amber)]"><Edit2 size={16} /></button>
                <button data-testid={`delete-part-${p.id}`} onClick={() => del(p.id)} className="p-2 text-[var(--text-muted)] hover:text-[#EF4444]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && <PartForm part={editing} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); onChange(); }} />}
    </div>
  );
}

function PartForm({ part, onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [images, setImages] = useState(part?.images || []);
  const [vehicle, setVehicle] = useState({
    make: part?.make || "", model: part?.model || "",
    year: part?.year_from || "", trim: part?.trim || "",
  });
  const [f, setF] = useState({
    name: part?.name || "", part_number: part?.part_number || "",
    category_id: part?.category_id || "", description: part?.description || "",
    condition: part?.condition || "مستعمل - درجة أولى", price: part?.price || "",
    quantity: part?.quantity || 1, featured: part?.featured || false,
  });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  const submit = async () => {
    if (!f.name || !vehicle.make || !vehicle.model || !f.category_id || !f.price) { toast.error("أكمل الحقول الأساسية"); return; }
    const yr = vehicle.year ? +vehicle.year : null;
    const payload = { ...f, make: vehicle.make, model: vehicle.model, trim: vehicle.trim || "", price: +f.price, quantity: +f.quantity, year_from: yr, year_to: yr, images };
    try {
      if (part) await api.put(`/parts/${part.id}`, payload);
      else await api.post("/parts", payload);
      toast.success(part ? "تم تحديث القطعة" : "تمت إضافة القطعة"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle className="text-white text-right">{part ? "تعديل القطعة" : "إضافة قطعة جديدة"}</DialogTitle>
          <DialogDescription className="text-right text-[var(--text-muted)]">أدخل بيانات القطعة وصورها.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">صور القطعة</label><ImageUploader images={images} setImages={setImages} /></div>
          <Inp label="اسم القطعة" testid="part-name" value={f.name} onChange={up("name")} />
          <Inp label="رقم القطعة" testid="part-number" value={f.part_number} onChange={up("part_number")} />
          <VehiclePicker value={vehicle} onChange={setVehicle} includeYear includeTrim />
          <Sel label="القسم" testid="part-category" value={f.category_id} onChange={up("category_id")} options={cats.map((c) => [c.id, c.name])} />
          <div className="grid grid-cols-2 gap-3">
            <Inp label={`السعر (${SAR})`} testid="part-price" type="number" value={f.price} onChange={up("price")} />
            <Inp label="الكمية" testid="part-quantity" type="number" value={f.quantity} onChange={up("quantity")} />
          </div>
          <Sel label="الحالة" testid="part-condition" value={f.condition} onChange={up("condition")} options={["مستعمل - درجة أولى", "مستعمل - درجة ثانية", "جديد", "مجدد"]} />
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">الوصف</label>
            <textarea data-testid="part-description" value={f.description} onChange={up("description")} rows={3} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none resize-none" /></div>
          <label className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <input type="checkbox" data-testid="part-featured" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> قطعة مميزة
          </label>
          <button data-testid="save-part-button" onClick={submit} className="btn-amber w-full justify-center">{part ? "حفظ التعديلات" : "إضافة القطعة"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrdersManager() {
  const [orders, setOrders] = useState(null);
  const load = () => api.get("/seller/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    let reason = "";
    if (status === "rejected") { reason = window.prompt("سبب الرفض:") || ""; }
    await api.put(`/orders/${id}/status`, { status, reason });
    toast.success("تم تحديث حالة الطلب"); load();
  };

  if (orders === null) return <Loader />;
  if (orders.length === 0) return <Empty text="لا توجد طلبات بعد." />;
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`seller-order-${o.id}`} className="ts-card p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-[var(--text-muted)]">#{o.order_number}</span>
            <StatusBadge status={o.status} />
          </div>
          <div className="flex gap-3">
            {o.part_snapshot?.image && <img src={fileUrl(o.part_snapshot.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />}
            <div className="flex-1">
              <p className="font-bold text-white text-sm">{o.part_snapshot?.name}</p>
              <p className="text-xs text-[var(--text-muted)]">العميل: {o.customer_name} · {o.customer_mobile}</p>
              <p className="text-xs text-[var(--text-muted)]">{o.delivery_method === "delivery" ? `توصيل: ${o.address}` : "استلام من التشليح"} · كمية {o.quantity}</p>
              {o.notes && <p className="text-xs text-[var(--text-subtle)] mt-1">ملاحظة: {o.notes}</p>}
            </div>
            <p className="font-head text-lg font-black text-[var(--amber)]">{o.total.toLocaleString("ar")} {SAR}</p>
          </div>
          <div className="mt-2"><PaymentBadge order={o} /></div>
          {NEXT_STATUS[o.status] && (
            <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--border-c)" }}>
              {NEXT_STATUS[o.status].map(([s, l]) => (
                <button key={s} data-testid={`order-action-${o.id}-${s}`} onClick={() => setStatus(o.id, s)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ${s === "rejected" ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[var(--amber)]/15 text-[var(--amber)]"}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const Inp = ({ label, testid, type = "text", ...rest }) => (
  <div><label className="text-xs text-[var(--text-muted)] block mb-1">{label}</label>
    <input data-testid={testid} type={type} {...rest} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none" /></div>
);
const Sel = ({ label, testid, options, ...rest }) => (
  <div><label className="text-xs text-[var(--text-muted)] block mb-1">{label}</label>
    <select data-testid={testid} {...rest} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none">
      <option value="">اختر</option>
      {options.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
    </select></div>
);

function RequestsForSeller() {
  const [reqs, setReqs] = useState(null);
  const [offerFor, setOfferFor] = useState(null);
  const load = () => api.get("/seller/part-requests").then(({ data }) => setReqs(data));
  useEffect(() => { load(); }, []);
  if (reqs === null) return <Loader />;
  if (reqs.length === 0) return <Empty text="لا توجد طلبات قطع من العملاء حاليًا." />;
  return (
    <div className="space-y-3">
      {reqs.map((r) => (
        <div key={r.id} data-testid={`seller-request-${r.id}`} className="ts-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3 min-w-0">
              {r.images?.[0] && <img src={fileUrl(r.images[0])} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
              <div className="min-w-0">
                <p className="font-bold text-white">{r.part_name}</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">{r.make} {r.model} {r.year ? `· ${r.year}` : ""} {r.trim ? `· ${r.trim}` : ""}</p>
                {r.description && <p className="text-xs text-[var(--text-subtle)] mt-1">{r.description}</p>}
                <p className="text-[11px] text-[var(--text-subtle)] mt-1">#{r.request_number} · {r.city || "كل المدن"} · {r.offers_count} عرض</p>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border-c)" }}>
            {r.my_offer ? (
              <span className="text-xs font-bold text-[var(--emerald)]">✓ قدّمت عرضك: {r.my_offer.price?.toLocaleString("ar")} {SAR}</span>
            ) : (
              <button data-testid={`make-offer-${r.id}`} onClick={() => setOfferFor(r)} className="btn-amber !py-2 !px-4 text-sm"><Send size={15} /> قدّم عرضًا</button>
            )}
          </div>
        </div>
      ))}
      {offerFor && <OfferForm req={offerFor} onClose={() => setOfferFor(null)} onDone={() => { setOfferFor(null); load(); }} />}
    </div>
  );
}

function OfferForm({ req, onClose, onDone }) {
  const [images, setImages] = useState([]);
  const [f, setF] = useState({ price: "", condition: "مستعمل - درجة أولى", warranty: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.price || +f.price <= 0) { toast.error("أدخل سعرًا صحيحًا"); return; }
    setBusy(true);
    try {
      await api.post(`/part-requests/${req.id}/offers`, { price: +f.price, condition: f.condition, warranty: f.warranty, notes: f.notes, images });
      toast.success("تم إرسال عرضك للعميل"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle className="text-white text-right">تقديم عرض على «{req.part_name}»</DialogTitle>
          <DialogDescription className="text-right text-[var(--text-muted)]">{req.make} {req.model} {req.year ? `· ${req.year}` : ""}</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <Inp label={`السعر (${SAR})`} testid="offer-price" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
          <Sel label="حالة القطعة" testid="offer-condition" value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} options={["مستعمل - درجة أولى", "مستعمل - درجة ثانية", "جديد", "مجدد"]} />
          <Inp label="الضمان (اختياري)" testid="offer-warranty" value={f.warranty} onChange={(e) => setF({ ...f, warranty: e.target.value })} />
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">ملاحظات (اختياري)</label>
            <textarea data-testid="offer-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none resize-none" /></div>
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">صور القطعة (اختياري)</label><ImageUploader images={images} setImages={setImages} /></div>
          <button data-testid="submit-offer-button" onClick={submit} disabled={busy} className="btn-amber w-full justify-center disabled:opacity-60">{busy ? "جارٍ الإرسال..." : "إرسال العرض"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const OFFER_STATUS = { pending: ["بانتظار رد العميل", "#F59E0B"], accepted: ["تم القبول 🎉", "#10B981"], declined: ["لم يُختَر", "#6B7280"] };

function MyOffers() {
  const [offers, setOffers] = useState(null);
  useEffect(() => { api.get("/seller/offers").then(({ data }) => setOffers(data)); }, []);
  if (offers === null) return <Loader />;
  if (offers.length === 0) return <Empty text="لم تقدّم أي عروض بعد. تصفّح «طلبات العملاء» وقدّم عروضك." />;
  return (
    <div className="space-y-3">
      {offers.map((o) => {
        const [lbl, color] = OFFER_STATUS[o.status] || OFFER_STATUS.pending;
        return (
          <div key={o.id} data-testid={`my-offer-${o.id}`} className="ts-card p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-bold text-white">{o.request?.part_name || "قطعة"}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{o.request?.make} {o.request?.model} {o.request?.year ? `· ${o.request.year}` : ""}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">الحالة: <span className="text-white">{o.condition}</span>{o.warranty ? ` · ضمان: ${o.warranty}` : ""}</p>
            </div>
            <div className="text-left shrink-0">
              <p className="font-head text-lg font-black text-[var(--amber)]">{o.price.toLocaleString("ar")} {SAR}</p>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg mt-1 inline-block" style={{ backgroundColor: `${color}22`, color }}>{lbl}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}


const PAYOUT_LABELS = {
  pending_verification: ["بانتظار التحقق", "#F59E0B"],
  submitted: ["تم الإرسال قيد المراجعة", "#F59E0B"],
  verified: ["تم التحقق", "#10B981"],
  ready_to_receive_payouts: ["جاهز لاستقبال المدفوعات", "#10B981"],
  rejected: ["مرفوض", "#EF4444"],
};

function PaymentInfo() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ iban: "", legal_name_ar: "", legal_name_en: "", cr_number: "", responsible_name: "", national_id: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/seller/payment-info").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;
  const st = data.payout_status || "pending_verification";
  const [lbl, color] = PAYOUT_LABELS[st] || PAYOUT_LABELS.pending_verification;
  const pi = data.payment_info;

  const save = async () => {
    if (!/^SA\d{22}$/.test(f.iban.replace(/\s/g, ""))) { toast.error("رقم الآيبان غير صحيح (SA + 22 رقمًا)"); return; }
    setSaving(true);
    try {
      await api.post("/seller/payment-info", f);
      toast.success("تم حفظ بيانات الدفع، بانتظار التحقق");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl space-y-4" data-testid="seller-payment-info">
      <div className="ts-card p-4 flex items-center justify-between">
        <div>
          <p className="font-bold text-white text-sm">حالة التحقق من بيانات الدفع</p>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">لا يمكنك استقبال المدفوعات الإلكترونية قبل اكتمال التحقق</p>
        </div>
        <span data-testid="payout-status-badge" className="text-xs font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${color}22`, color }}>{lbl}</span>
      </div>

      {pi?.rejection_reason && st === "rejected" && (
        <div className="ts-card p-3 border" style={{ borderColor: "#EF444455" }}>
          <p className="text-xs text-[#EF4444]">سبب الرفض: {pi.rejection_reason}</p>
        </div>
      )}

      {!data.marketplace_enabled && (
        <div className="ts-card p-3 bg-[var(--amber)]/5">
          <p className="text-xs text-[var(--amber)]">سيتم ربط حسابك مع Tap Marketplace وإنشاء وجهة الدفع (Destination) بعد تفعيل الخدمة واكتمال التحقق من قِبل Tap.</p>
        </div>
      )}

      {pi && (
        <div className="ts-card p-4 text-sm space-y-1">
          <p className="text-white font-bold mb-1">البيانات المحفوظة</p>
          <p className="text-[var(--text-muted)] text-xs">الآيبان: <span className="text-white">{pi.iban_last4}</span></p>
          <p className="text-[var(--text-muted)] text-xs">السجل التجاري: <span className="text-white">{pi.cr_number}</span></p>
          <p className="text-[var(--text-muted)] text-xs">المسؤول: <span className="text-white">{pi.responsible_name}</span> (هوية تنتهي بـ {pi.national_id_last4})</p>
        </div>
      )}

      <div className="ts-card p-5 space-y-3">
        <p className="font-bold text-white text-sm">بيانات الدفع والتحويل (مطلوبة لـ Tap KYC)</p>
        <div><label className="text-xs text-[var(--text-muted)] block mb-1">رقم الآيبان IBAN</label>
          <input data-testid="pi-iban" dir="ltr" value={f.iban} onChange={(e) => setF({ ...f, iban: e.target.value })} placeholder="SA0000000000000000000000" className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">الاسم القانوني (عربي)</label>
            <input data-testid="pi-legal-ar" value={f.legal_name_ar} onChange={(e) => setF({ ...f, legal_name_ar: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">Legal name (EN)</label>
            <input data-testid="pi-legal-en" dir="ltr" value={f.legal_name_en} onChange={(e) => setF({ ...f, legal_name_en: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">رقم السجل التجاري</label>
            <input data-testid="pi-cr" value={f.cr_number} onChange={(e) => setF({ ...f, cr_number: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">اسم المسؤول (KYC)</label>
            <input data-testid="pi-responsible" value={f.responsible_name} onChange={(e) => setF({ ...f, responsible_name: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">رقم الهوية الوطنية</label>
            <input data-testid="pi-national-id" value={f.national_id} onChange={(e) => setF({ ...f, national_id: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
          <div><label className="text-xs text-[var(--text-muted)] block mb-1">جوال المسؤول</label>
            <input data-testid="pi-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
        </div>
        <div><label className="text-xs text-[var(--text-muted)] block mb-1">البريد الإلكتروني</label>
          <input data-testid="pi-email" dir="ltr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" /></div>
        <p className="text-xs text-[var(--text-subtle)]">ملاحظة: سيُطلب لاحقًا رفع شهادة الآيبان وصورة السجل التجاري وهوية المسؤول عند تفعيل Tap Marketplace.</p>
        <button data-testid="save-payment-info" onClick={save} disabled={saving} className="btn-amber w-full justify-center disabled:opacity-60">{saving ? "جارٍ الحفظ..." : "حفظ وإرسال للتحقق"}</button>
      </div>
    </div>
  );
}
